import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X, RefreshCw, WifiOff, CheckCircle } from "lucide-react";
import { InnerShadowTopIcon } from "@/utilities/icon-utils";
import { usePWA } from "@/hooks/use-pwa";
import { pwaUpdateManager } from "@/utilities/pwa-update-manager";
import { cn, layout, typography } from "@/utilities/tailwind-utils";

/**
 * Offline and app-update toasts. Service worker registration runs from `main.tsx` via `initServiceWorkerRegistration`; this component uses `usePWA()` for shared state only.
 */
export function PWAUpdateNotification() {
  const { isOnline, needRefresh, updatePWA, dismissUpdate } = usePWA();
  const [showOffline, setShowOffline] = useState(false);
  const [offlineVisible, setOfflineVisible] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [updateVisible, setUpdateVisible] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Enter/exit animation via cancellable timers only (no sync setState in the effect body).
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    if (!isOnline) {
      timers.push(setTimeout(() => setShowOffline(true), 0));
      timers.push(setTimeout(() => setOfflineVisible(true), 100));
    } else {
      timers.push(setTimeout(() => setOfflineVisible(false), 0));
      timers.push(setTimeout(() => setShowOffline(false), 300));
    }

    return () => timers.forEach(clearTimeout);
  }, [isOnline]);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const shouldShow = needRefresh && pwaUpdateManager.shouldShowUpdate();

    if (shouldShow) {
      timers.push(setTimeout(() => setShowUpdate(true), 0));
      timers.push(setTimeout(() => setUpdateVisible(true), 100));
    } else if (!needRefresh) {
      timers.push(setTimeout(() => setUpdateVisible(false), 0));
      timers.push(setTimeout(() => setShowUpdate(false), 300));
    }

    return () => timers.forEach(clearTimeout);
  }, [needRefresh]);

  const handleApplyUpdate = useCallback(async () => {
    setIsUpdating(true);
    try {
      await updatePWA();
    } catch {
      /* usePWA logs in debug */
    }
    setTimeout(() => {
      window.location.reload();
    }, 400);
  }, [updatePWA]);

  const handleDismissUpdate = useCallback(() => {
    setUpdateVisible(false);
    setTimeout(() => {
      setShowUpdate(false);
      dismissUpdate();
    }, 300);
  }, [dismissUpdate]);

  return (
    <>
      {showOffline && (
        <div
          className={cn(
            "fixed bottom-4 right-4 z-50 transition-all duration-300 ease-in-out",
            offlineVisible
              ? "translate-x-0 opacity-100"
              : "translate-x-full opacity-0"
          )}
        >
          <Card
            className={cn(
              "relative overflow-hidden border-0 shadow-xl",
              "bg-gradient-to-r from-orange-50 to-amber-50",
              "dark:from-orange-950/30 dark:to-amber-950/30",
              "backdrop-blur-sm border border-orange-200/50 dark:border-orange-800/50 py-3"
            )}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-amber-500/10 animate-pulse" />

            <div className={cn("relative p-4", layout.flexCenterGap3)}>
              <div
                className={cn(
                  "flex-shrink-0 w-10 h-10 rounded-full",
                  "bg-orange-100 dark:bg-orange-900/50",
                  "flex items-center justify-center"
                )}
              >
                <WifiOff className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>

              <div className={cn("flex-1 min-w-0", layout.flexColGap1)}>
                <div className={cn(layout.flexCenterGap2)}>
                  <h4
                    className={cn(
                      typography.fontSemibold,
                      "text-orange-900 dark:text-orange-100"
                    )}
                  >
                    You&apos;re offline
                  </h4>
                  <Badge
                    variant="outline"
                    className="text-orange-700 border-orange-300 dark:text-orange-300 dark:border-orange-700"
                  >
                    Offline
                  </Badge>
                </div>
                <p
                  className={cn(
                    typography.textSm,
                    "text-orange-700 dark:text-orange-200/80"
                  )}
                >
                  Some features may be limited. Check your connection.
                </p>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowOffline(false)}
                className={cn(
                  "flex-shrink-0 h-8 w-8 p-0 rounded-full",
                  "text-orange-600 hover:text-orange-700",
                  "hover:bg-orange-100 dark:hover:bg-orange-900/50",
                  "transition-colors duration-200"
                )}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        </div>
      )}

      {showUpdate && (
        <div
          className={cn(
            "fixed bottom-4 right-4 z-[60] transition-all duration-300 ease-in-out",
            updateVisible
              ? "translate-x-0 opacity-100"
              : "translate-x-full opacity-0"
          )}
        >
          <Card
            className={cn(
              "relative overflow-hidden border-0 shadow-xl",
              "bg-white/80 dark:bg-neutral-900/80",
              "border border-neutral-200/60 dark:border-neutral-800/60",
              "backdrop-blur-lg"
            )}
            style={{ backdropFilter: "blur(16px)" }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-indigo-500/5 animate-pulse pointer-events-none" />

            <div className={cn("relative p-4", layout.flexCenterGap3)}>
              <div
                className={cn(
                  "flex-shrink-0 w-10 h-10 rounded-full",
                  "bg-blue-100 dark:bg-blue-900/50",
                  "flex items-center justify-center"
                )}
              >
                {isUpdating ? (
                  <RefreshCw className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
                ) : (
                  <InnerShadowTopIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                )}
              </div>

              <div className={cn("flex-1 min-w-0", layout.flexColGap1)}>
                <div className={cn(layout.flexCenterGap2)}>
                  <h4
                    className={cn(
                      typography.fontSemibold,
                      "text-blue-900 dark:text-blue-100"
                    )}
                  >
                    {isUpdating ? "Updating..." : "Update available"}
                  </h4>
                  <Badge
                    variant="outline"
                    className="text-blue-700 border-blue-300 dark:text-blue-300 dark:border-blue-700"
                  >
                    {isUpdating ? "Updating" : "New"}
                  </Badge>
                </div>
                <p
                  className={cn(
                    typography.textSm,
                    "text-blue-700 dark:text-blue-200/80"
                  )}
                >
                  {isUpdating
                    ? "Installing the latest version..."
                    : "A new version is ready. Update now for the latest features."}
                </p>
              </div>

              <div className={cn("flex-shrink-0", layout.flexCenterGap2)}>
                {!isUpdating && (
                  <Button
                    size="sm"
                    onClick={() => void handleApplyUpdate()}
                    disabled={isUpdating}
                    className={cn(
                      "bg-blue-600 hover:bg-blue-700",
                      "text-white shadow-sm",
                      "transition-all duration-200",
                      "hover:shadow-md active:scale-95"
                    )}
                  >
                    Update
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDismissUpdate}
                  disabled={isUpdating}
                  className={cn(
                    "flex-shrink-0 h-8 w-8 p-0 rounded-full",
                    "text-blue-600 hover:text-blue-700",
                    "hover:bg-blue-100 dark:hover:bg-blue-900/50",
                    "transition-colors duration-200"
                  )}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

export function PWAOnlineStatus() {
  const [isOnline, setIsOnline] = useState(
    () => typeof navigator !== "undefined" && navigator.onLine
  );
  const wasOfflineRef = useRef(
    typeof navigator !== "undefined" && !navigator.onLine
  );
  const [showOnline, setShowOnline] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const onOffline = () => {
      wasOfflineRef.current = true;
      setIsOnline(false);
    };
    const onOnline = () => {
      setIsOnline(true);
      if (wasOfflineRef.current) {
        wasOfflineRef.current = false;
        setShowOnline(true);
        setTimeout(() => setIsVisible(true), 100);
        setTimeout(() => {
          setIsVisible(false);
          setTimeout(() => setShowOnline(false), 300);
        }, 3000);
      }
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  if (!showOnline || !isOnline) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 z-50 transition-all duration-300 ease-in-out",
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      )}
    >
      <Card
        className={cn(
          "relative overflow-hidden border-0 shadow-xl",
          "bg-white/80 dark:bg-neutral-900/80",
          "border border-neutral-200/60 dark:border-neutral-800/60",
          "backdrop-blur py-3"
        )}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-emerald-500/10 animate-pulse" />

        <div className={cn("relative p-4", layout.flexCenterGap3)}>
          <div
            className={cn(
              "flex-shrink-0 w-10 h-10 rounded-full",
              "bg-green-100 dark:bg-green-900/50",
              "flex items-center justify-center"
            )}
          >
            <InnerShadowTopIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>

          <div className={cn("flex-1 min-w-0", layout.flexColGap1)}>
            <div className={cn(layout.flexCenterGap2)}>
              <h4
                className={cn(
                  typography.fontSemibold,
                  "text-green-900 dark:text-green-100"
                )}
              >
                Back online
              </h4>
              <Badge
                variant="outline"
                className="text-green-700 border-green-300 dark:text-green-300 dark:border-green-700"
              >
                Online
              </Badge>
            </div>
            <p
              className={cn(
                typography.textSm,
                "text-green-700 dark:text-green-200/80"
              )}
            >
              Connection restored. All features are now available.
            </p>
          </div>

          <div
            className={cn(
              "flex-shrink-0 h-8 w-8",
              "flex items-center justify-center"
            )}
          >
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
        </div>
      </Card>
    </div>
  );
}
